import { useRef } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Car } from '../../car';
import CarCarouselCardSkeleton from '../../skeletons/CarCarouselCardSkeleton';
import Skeleton from '../../Skeleton';
import { ApolloError } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Colors, DesignTokens } from '../../../styles/colors';
import { carouselCardScrollAmount } from '../../car/carCarouselCardWidth';
import { CarCardPriceSlot } from '../../../utils/carCardPrice';

type CarsSlideShowButton = { text: string; onClick: () => void };

export type CarsSlideShowProps<
  T extends { id: string; isCreated?: boolean | null; thumbnailUrl?: string | null },
> = {
  cars: readonly T[] | null | undefined;
  carsLoading: boolean;
  carsError: ApolloError | undefined;
  renderButtons: (car: T) => CarsSlideShowButton[];
  asDealer?: boolean;
  onCarClick?: (carId: string) => void;
  title?: string;
  /** Spec 022: daily, weekly (week price) or monthly. */
  priceMode?: CarCardPriceSlot;
  /** Spec 028: a "View all" link before the arrows (home monthly sections). */
  viewAll?: { onClick: () => void; label?: string; testId?: string };
};

const FullContainer = styled.div`
  ${tw`
    w-full
    max-w-full
    self-stretch
    flex
    flex-col
    min-w-0
    overflow-x-hidden
    box-border
  `};
`;

const HeaderContainer = styled.div`
  ${tw`
    flex
    w-full
    flex-nowrap
    items-center
    mb-3
    px-4
    md:px-0
  `};
  gap: 12px;
`;

const Title = styled.h2`
  ${tw`flex-1 min-w-0`}
  font-family: ${DesignTokens.typography.fontFamilyDisplay};
  font-size: ${DesignTokens.typography.fontSize.title1};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  line-height: ${DesignTokens.typography.lineHeight.title1};
  color: ${Colors.TextPrimary};
  margin: 0;
`;

const ViewAllButton = styled.button`
  ${tw`flex-shrink-0 cursor-pointer whitespace-nowrap`}
  background: none;
  border: 0;
  padding: 8px 4px;
  font-size: ${DesignTokens.typography.fontSize.bodySm};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${Colors.TextPrimary};
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: ${Colors.TextSecondary};
  }
`;

const ArrowsContainer = styled.div`
  ${tw`
    flex
    items-center
    flex-shrink-0
    ml-auto
  `};
  gap: 12px;
`;

const ArrowButton = styled.button`
  ${tw`
    flex
    items-center
    justify-center
    cursor-pointer
  `}
  width: 40px;
  height: 40px;
  background: ${Colors.White};
  border: 1px solid ${Colors.BorderLight};
  border-radius: 8px;
  color: ${Colors.TextSecondary};
  transition: all 150ms ease-out;

  &:hover {
    border-color: ${Colors.Gold};
    color: ${Colors.Gold};
  }

  svg {
    font-size: 14px;
  }
`;

const CarouselViewport = styled.div`
  ${tw`
    w-full
    max-w-full
    min-w-0
    overflow-hidden
    box-border
  `};
`;

const CarsContainer = styled.div`
  ${tw`
    w-full
    max-w-full
    min-w-0
    flex
    overflow-x-auto
    overscroll-x-contain
    pb-2
    px-4
    md:px-0
    scroll-smooth
    box-border
  `};
  gap: 16px;

  /* Hide scrollbar but allow scrolling */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;

const EmptyCars = styled.div`
  ${tw`
    w-full
    flex
    justify-center
    items-center
    py-10
  `};
  font-size: ${DesignTokens.typography.fontSize.bodySm};
  line-height: ${DesignTokens.typography.lineHeight.body};
  color: ${Colors.TextSecondary};
`;

const CAROUSEL_SKELETON_COUNT = 4;

const ArrowSkeleton = styled(Skeleton)`
  width: 40px;
  height: 40px;
  border-radius: 8px;
`;

function CarsSlideShow<
  T extends { id: string; isCreated?: boolean | null; thumbnailUrl?: string | null },
>({
  cars,
  carsLoading,
  carsError,
  renderButtons,
  asDealer = false,
  onCarClick,
  title,
  priceMode = 'daily',
  viewAll,
}: CarsSlideShowProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (containerRef.current) {
      const scrollAmount = carouselCardScrollAmount();
      containerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const allCars =
    cars
      ?.map((car, index) => {
        if (car.isCreated) {
          return (
            <Car
              key={`${index}-${car.id}`}
              asDealer={asDealer}
              {...(car as any)}
              thumbnailSrc={car.thumbnailUrl}
              renderButtons={() => renderButtons(car)}
              onClick={() => onCarClick && onCarClick(car.id)}
              showButtons={false} // Match mobile design: no buttons on cards in slider
              priceMode={priceMode}
            />
          );
        }
        return null;
      })
      .filter((node): node is JSX.Element => node != null) || [];

  const isEmptyTopCars = !allCars || allCars?.length === 0;

  if (carsLoading) {
    return (
      <FullContainer aria-busy="true" aria-label={title ? `${title} loading` : 'Cars loading'}>
        <HeaderContainer>
          {title ? <Title>{title}</Title> : <div />}
          <ArrowsContainer>
            <ArrowSkeleton />
            <ArrowSkeleton />
          </ArrowsContainer>
        </HeaderContainer>
        <CarouselViewport>
          <CarsContainer>
            {Array.from({ length: CAROUSEL_SKELETON_COUNT }, (_, index) => (
              <CarCarouselCardSkeleton key={index} />
            ))}
          </CarsContainer>
        </CarouselViewport>
      </FullContainer>
    );
  }

  if (carsError) {
    return <EmptyCars>Error loading cars: {carsError.message}</EmptyCars>;
  }

  if (isEmptyTopCars) {
    return <EmptyCars>No Cars To Show!</EmptyCars>;
  }

  return (
    <FullContainer>
      <HeaderContainer>
        {title ? <Title>{title}</Title> : <div />}
        <ArrowsContainer>
          {viewAll && (
            <ViewAllButton
              type="button"
              onClick={viewAll.onClick}
              data-testid={viewAll.testId}
              aria-label={title ? `${viewAll.label ?? 'View all'}: ${title}` : undefined}
            >
              {viewAll.label ?? 'View all'}
            </ViewAllButton>
          )}
          <ArrowButton onClick={() => scroll('left')} aria-label="Previous">
            <FontAwesomeIcon icon={faChevronLeft} />
          </ArrowButton>
          <ArrowButton onClick={() => scroll('right')} aria-label="Next">
            <FontAwesomeIcon icon={faChevronRight} />
          </ArrowButton>
        </ArrowsContainer>
      </HeaderContainer>
      <CarouselViewport>
        <CarsContainer ref={containerRef}>{allCars}</CarsContainer>
      </CarouselViewport>
    </FullContainer>
  );
}

export default CarsSlideShow;
